import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MANUAL_VERSION = "1.7-D";
const IS_DRAFT = MANUAL_VERSION.endsWith("-D");

const LANES = [
  "Ops/Execution — Valley of Fire, LLC",
  "BD/Clinical — Excel Healthcare Consulting Services, LLC",
  "Finance/Legal/Compliance — Gila Holdings, LLC",
  "G&A Administrator",
  "Contracted Assist (PA-C / NPRNFA / CSA)",
  "Other / Vendor",
];

export default function AckPage({ active }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [lane, setLane] = useState("");
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [log, setLog] = useState(null);
  const [reads, setReads] = useState(null);
  const REQUIRED = Array.from({ length: 23 }, (_, i) => `c${i}`);

  const fetchLog = useCallback(() => {
    axios.get(`${API}/acknowledgments`).then((r) => setLog(r.data)).catch(() => setLog([]));
    axios.get(`${API}/chapter-reads`).then((r) => setReads(r.data)).catch(() => setReads({}));
  }, []);

  useEffect(() => {
    if (active === "ack") fetchLog();
  }, [active, fetchLog]);

  const confirmedCount = reads ? REQUIRED.filter((id) => reads[id]).length : 0;
  const gateOpen = reads !== null && confirmedCount === REQUIRED.length;
  const canSubmit = name.trim() && email.trim() && lane && signature.trim() && agreed && gateOpen && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/acknowledgments`, {
        name, email, role_lane: lane, signature, manual_version: MANUAL_VERSION,
      });
      toast.success(`Acknowledgment recorded for ${name} — manual v${MANUAL_VERSION}`);
      setName(""); setEmail(""); setLane(""); setSignature(""); setAgreed(false);
      fetchLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record acknowledgment");
    }
    setSubmitting(false);
  };

  return (
    <section className={`chapter utility${active === "ack" ? " active" : ""}`} id="ack">
      <div className="eyebrow">Working record · ECH-SOP-1.1</div>
      <h1>Manual Acknowledgment &amp; Log</h1>
      <p className="lede">Every workforce member acknowledges the current manual version within 10 business days of issue. This page is the electronic acknowledgment and the running log the Compliance Officer reconciles to the workforce roster quarterly.</p>

      <div className="formcard" data-testid="ack-form">
        <div className="formcard-title">Sign acknowledgment — manual v{MANUAL_VERSION}</div>
        {IS_DRAFT && (
          <div className="ack-gate" data-testid="ack-draft-note" style={{ marginBottom: 12 }}>
            This manual is a <b>DRAFT</b> (v{MANUAL_VERSION} — pending counsel sign-off). Signatures recorded now are <b>draft reads</b>: they evidence review of the draft, not acknowledgment of a final policy. When the FINAL version issues, SOP-1.1 requires everyone to re-acknowledge within 10 business days — draft reads do not carry over.
          </div>
        )}
        <div className="fld-grid">
          <div className="fld">
            <label>Full name</label>
            <input className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" data-testid="ack-name" />
          </div>
          <div className="fld">
            <label>Email</label>
            <input className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@extendedcoveragehealthcare.com" data-testid="ack-email" />
          </div>
          <div className="fld">
            <label>Role / lane</label>
            <Select value={lane} onValueChange={setLane}>
              <SelectTrigger className="inp sel" data-testid="ack-lane"><SelectValue placeholder="Select role or lane…" /></SelectTrigger>
              <SelectContent>
                {LANES.map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="fld">
            <label>Typed signature</label>
            <input className="inp sig" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Type your full name as signature" data-testid="ack-signature" />
          </div>
        </div>
        <label className="agree-row" data-testid="ack-agree-row">
          <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} data-testid="ack-agree" />
          <span>I have read and understood the ECH Operations, Compliance &amp; SOP Manual (v{MANUAL_VERSION}), including the Code of Conduct, and agree to comply with its policies. I understand that the three rules above every rule admit no exceptions.</span>
        </label>
        {reads !== null && (
          <div className={`ack-gate${gateOpen ? " open" : ""}`} data-testid="ack-gate">
            {gateOpen
              ? `✓ Sign-off gate open — all ${REQUIRED.length} sections confirmed read & understood under your account.`
              : `Sign-off gate: ${confirmedCount} of ${REQUIRED.length} sections confirmed read & understood. Finish every chapter (button at the bottom of each section) before signing — the signature attests you actually read it.`}
          </div>
        )}
        <button className="btn" onClick={submit} disabled={!canSubmit} data-testid="ack-submit">Sign &amp; record acknowledgment</button>
      </div>

      <h2>Acknowledgment log</h2>
      <table data-testid="ack-log">
        <tbody>
          <tr><th>Name</th><th>Email</th><th>Role / lane</th><th>Version</th><th>Signature</th><th>Signed</th><th>Account (identity)</th></tr>
          {log === null ? (
            <tr><td colSpan={7}>Loading…</td></tr>
          ) : log.length === 0 ? (
            <tr><td colSpan={7}>No acknowledgments recorded yet.</td></tr>
          ) : (
            log.map((a) => (
              <tr key={a.id}>
                <td>{a.name}</td>
                <td>{a.email}</td>
                <td>{a.role_lane}</td>
                <td className="q">{a.manual_version}{String(a.manual_version || "").endsWith("-D") && <div><span className="vr-pill open" data-testid={`ack-draft-pill-${a.id}`}>DRAFT READ</span></div>}</td>
                <td style={{ fontStyle: "italic" }}>“{a.signature}”</td>
                <td>{new Date(a.acknowledged_at).toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                <td data-testid={`ack-account-${a.id}`}>{a.recorded_by ? <>{a.recorded_by.name}<div className="cap-sub">{a.recorded_by.email}</div></> : "—"}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Retention: 7 years (ECH-SOP-1.1). The Compliance Officer reconciles this log against the workforce roster quarterly.</p>
    </section>
  );
}
